import React, { useEffect, useState } from 'react';

import { TextEditorWrapper } from '@components/shared/TextEditorWrapper';
import { useTiptapFromDocumentId } from '@components/shared/Tiptap';
import { UploadFileModal } from '@components/shared/UploadFileModal';
import { uploadToS3 } from '@components/shared/uploadToS3';
import { useToaster } from '@components/stores/toaster';
import { api } from '@components/SurveyBuilder';
import { useFeature } from '@hooks/useFeature';

import { failedUpdate, successUpdate } from '../../toasts';

import { InsightCoverImage } from './InsightCoverImage';

const FileTypesText = () => (
  <div className='flex flex-col items-center'>
    <span className='text-xs'>We support JPEG and PNG files only.</span>
    <span className='text-xs'>Recommended size is 884x264 px.</span>
  </div>
);

interface Props {
  insight: Insight;
  loading: boolean;
  setSaving: (saving: boolean) => void;
}

export const InsightEditor: React.FC<React.PropsWithChildren<Props>> = ({ insight, loading, setSaving }) => {
  const [attachImageOpen, setAttachImageOpen] = useState<boolean>(false);
  const [uploading, setUploading] = useState(false);

  const [updateInsightCoverImage] = api.useUpdateInsightCoverImageMutation();
  const [deleteInsightCoverImage] = api.useDeleteInsightCoverImageMutation();

  const showToast = useToaster();

  const handleDrop = async (file: File[]) => {
    try {
      setAttachImageOpen(false);
      setUploading(true);
      const signedId = await uploadToS3(file[0]);
      await updateInsightCoverImage({ slug: insight.slug, signed_id: signedId }).unwrap();
      showToast(successUpdate());
    } catch {
      showToast(failedUpdate());
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      setUploading(true);
      await deleteInsightCoverImage({ slug: insight.slug }).unwrap();
      showToast(successUpdate());
    } catch {
      showToast(failedUpdate());
    } finally {
      setUploading(false);
    }
  };

  const tiptap = useTiptapFromDocumentId({
    documentId: insight.document_id,
    studyId: insight.project_id,
    liveUpdate: true,
    config: {
      import_document: useFeature('import_document'),
      columns: true,
      image: { enable: true },
      link: { enable: true },
      artifacts: true,
      templates: true,
      placeholder: 'Write learnings, embed interviews, and so much more…',
      tables: true
    }
  });

  useEffect(() => {
    setSaving(tiptap.isLoading ?? false);
  }, [tiptap.isLoading]);

  return (
    <>
      <TextEditorWrapper
        saving={loading}
        tiptap={tiptap}
        renderCover={() => (
          <InsightCoverImage
            openAttachModal={() => setAttachImageOpen(true)}
            onRemove={handleRemoveImage}
            imageUrl={insight.cover_image_url}
          />
        )}
      />
      <UploadFileModal
        uploading={uploading}
        heading={`${insight.cover_image_url ? 'Change' : 'Add'} cover photo`}
        subheading='Larger images will be automatically cropped to visible size.'
        fileTypesText={<FileTypesText />}
        supportedFileTypes={['png', 'jpeg', 'jpg']}
        open={attachImageOpen}
        onClose={() => setAttachImageOpen(false)}
        onDrop={handleDrop}
      />
    </>
  );
};
